import has from 'lodash/has';
import React, { useState } from 'react';
import { Feature, SYSTEM_ISO_CODES } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paper, Tab, Tabs, TextField, Theme } from '@mui/material';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { createStyles, makeStyles } from '@mui/styles';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faProjectDiagram } from '@fortawesome/pro-solid-svg-icons/faProjectDiagram';
import { faText } from '@fortawesome/pro-solid-svg-icons/faText';
import { faUsdCircle } from '@fortawesome/pro-solid-svg-icons/faUsdCircle';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EarningRuleFormValue } from './form';
import { EarningRulePanelCriteria } from './EarningRulePanelCriteria';
import { EarningRulePanelEarnings } from './EarningRulePanelEarnings';
import { RulePanelDescription } from '../rules/components';
import { useFeatures } from '../features/FeaturesContext';

interface StyleProps {
  tab: string;
}

const styles = ({ spacing }: Theme) => {
  return createStyles<string, StyleProps>({
    panel: {
      border: `1px solid ${colorPalette.grey.main}`,
    },
    panelContent: {
      padding: spacing(2),
    },
    panelDescription: {},
    panelTrigger: {},
    tabs: {
      flex: 1,
    },
    actions: {
      alignItems: 'stretch',
      display: 'flex',
      justifyContent: 'space-between',
    },
    errorIcon: {
      marginLeft: spacing(0.5),
    },
  });
};

const useStyles = makeStyles(styles);

enum TabEnum {
  TRIGGER = 'TRIGGER',
  EARNINGS = 'EARNINGS',
  DESCRIPTION = 'DESCRIPTION',
}

const descriptionKeys = SYSTEM_ISO_CODES.map(isoCode => `i18nAttributes.${isoCode}.description`);

export function EarningRulePanels(): React.ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState(TabEnum.TRIGGER);
  const classes = useStyles({ tab });
  const {
    formState: { errors },
    register,
  } = useFormContext<EarningRuleFormValue>();
  const { features } = useFeatures();
  const { write: earningRuleWrite } = features[Feature.EARNING_RULE];

  const isTab = (name: string) => name === tab;
  const hasErrors = (...keys: string[]) => keys.some(key => has(errors, key));
  return (
    <div>
      <TextField
        {...register('name')}
        fullWidth
        placeholder={t('earning-rules.form.fields.name')}
        sx={{ marginBottom: 2 }}
        error={!!errors.name}
        helperText={errors.name?.message}
      />
      <Paper className={classes.panel}>
        <div className={classes.actions}>
          <Tabs className={classes.tabs} value={tab} onChange={(_, value) => setTab(value)}>
            <Tab
              value={TabEnum.TRIGGER}
              label={
                <>
                  {t('earning-rules.tabs.trigger')}
                  {hasErrors('eventSource', 'eventName', 'criteria') && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}{' '}
                </>
              }
              icon={<FontAwesomeIcon icon={faProjectDiagram} />}
            />
            <Tab
              value={TabEnum.EARNINGS}
              id={'earning-rule-earnings-id'}
              label={
                <>
                  {t('earning-rules.tabs.earnings')}
                  {hasErrors('actions') && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}{' '}
                </>
              }
              icon={<FontAwesomeIcon icon={faUsdCircle} />}
            />
            <Tab
              value={TabEnum.DESCRIPTION}
              label={
                <>
                  {t('earning-rules.tabs.description')}
                  {hasErrors(...descriptionKeys) && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}
                </>
              }
              icon={<FontAwesomeIcon icon={faText} />}
            />
          </Tabs>
        </div>
        <div className={classes.panelContent}>
          {isTab(TabEnum.TRIGGER) && <EarningRulePanelCriteria className={classes.panelTrigger} />}
          {isTab(TabEnum.EARNINGS) && <EarningRulePanelEarnings className={classes.panelTrigger} />}
          {isTab(TabEnum.DESCRIPTION) && (
            <RulePanelDescription
              disabled={!earningRuleWrite}
              type={RuleType.EARN}
              className={classes.panelDescription}
            />
          )}
        </div>
      </Paper>
    </div>
  );
}
